import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';

export const fetchTransactions = createAsyncThunk(
  'transaction/fetchTransactions',
  async (filters, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/transactions', { params: filters });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch transactions');
    }
  }
);

export const createTransaction = createAsyncThunk(
  'transaction/createTransaction',
  async (transactionData, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/transactions', transactionData);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to create transaction');
    }
  }
);

const initialState = {
  transactions: [],
  selectedTransaction: null,
  loading: false,
  error: null,
};

const transactionSlice = createSlice({
  name: 'transaction',
  initialState,
  reducers: {
    selectTransaction: (state, action) => {
      state.selectedTransaction = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload || [];
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.transactions = [];
        state.error = action.payload || 'Failed to fetch transactions';
      })
      .addCase(createTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions.unshift(action.payload);
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create transaction';
      });
  },
});

export const { selectTransaction, clearError } = transactionSlice.actions;
export default transactionSlice.reducer;

export const selectTransactions = (state) => state.transaction.transactions;
export const selectTransactionLoading = (state) => state.transaction.loading;
export const selectTransactionError = (state) => state.transaction.error;

// Hook
export function useTransaction() {
  const dispatch = useDispatch();
  const transactions = useSelector(selectTransactions);
  const loading = useSelector(selectTransactionLoading);
  const error = useSelector(selectTransactionError);

  return {
    transactions,
    loading,
    error,
    fetchTransactions: useCallback((filters) => dispatch(fetchTransactions(filters)), [dispatch]),
    createTransaction: useCallback((data) => dispatch(createTransaction(data)), [dispatch]),
  };
}
